import { GreenAutoMapParams } from 'utils/gpsEditor/gpsUtils'

const GREEN_AUTO_MAP_LEVEL_LABELS = ['Strictest', 'Tight', 'Medium', 'Loose', 'Ultimate'] as const

export const formatGreenAutoMapLevelValue = (value: number): string => {
  if (value < 0) {
    return 'Auto'
  }

  if (value < GREEN_AUTO_MAP_LEVEL_LABELS.length) {
    return GREEN_AUTO_MAP_LEVEL_LABELS[value]
  }

  return `Ultimate +${value - (GREEN_AUTO_MAP_LEVEL_LABELS.length - 1)}`
}

export type GreenAutoMapParamKey = keyof GreenAutoMapParams
export type GreenAutoMapNumericParamKey = {
  [K in GreenAutoMapParamKey]: GreenAutoMapParams[K] extends number ? K : never
}[GreenAutoMapParamKey]
export type GreenAutoMapToggleParamKey = {
  [K in GreenAutoMapParamKey]: GreenAutoMapParams[K] extends boolean ? K : never
}[GreenAutoMapParamKey]

export interface GreenAutoMapSliderConfig {
  key: GreenAutoMapNumericParamKey
  label: string
  min: number
  max: number
  step: number
  formatValue?: (value: number) => string
}

export interface GreenAutoMapToggleConfig {
  key: GreenAutoMapToggleParamKey
  label: string
  description: string
  shouldRemap?: boolean
}

export const GREEN_AUTO_MAP_PRIMARY_SLIDERS: GreenAutoMapSliderConfig[] = [
  {
    key: 'lPresetIndex',
    label: 'L params',
    min: -1,
    max: 10,
    step: 1,
    formatValue: formatGreenAutoMapLevelValue,
  },
  {
    key: 'abPresetIndex',
    label: 'AB params',
    min: -1,
    max: 10,
    step: 1,
    formatValue: formatGreenAutoMapLevelValue,
  },
  {
    key: 'rayCount',
    label: 'Rays',
    min: 12,
    max: 36,
    step: 1,
    formatValue: (value) => `${value.toFixed(0)}`,
  },
]

export const GREEN_AUTO_MAP_ADVANCED_SLIDERS: GreenAutoMapSliderConfig[] = [
  {
    key: 'loosenessBias',
    label: 'Looseness',
    min: -4,
    max: 10,
    step: 1,
    formatValue: (value) => `${value > 0 ? '+' : ''}${value.toFixed(0)}`,
  },
  {
    key: 'maxSearchRadiusM',
    label: 'Search radius',
    min: 15,
    max: 80,
    step: 1,
    formatValue: (value) => `${value.toFixed(0)} m`,
  },
  {
    key: 'abGate',
    label: 'AB gate',
    min: 3,
    max: 8,
    step: 0.25,
    formatValue: (value) => value.toFixed(2),
  },
  {
    key: 'stepPx',
    label: 'Step size',
    min: 4,
    max: 20,
    step: 1,
    formatValue: (value) => `${value.toFixed(0)} px`,
  },
  {
    key: 'patchHalfPx',
    label: 'Patch half-size',
    min: 6,
    max: 30,
    step: 1,
    formatValue: (value) => `${value.toFixed(0)} px`,
  },
  {
    key: 'probeRadiusM',
    label: 'Probe radius',
    min: 0.4,
    max: 2.0,
    step: 0.1,
    formatValue: (value) => `${value.toFixed(1)} m`,
  },
]

export const GREEN_AUTO_MAP_ADVANCED_TOGGLES: GreenAutoMapToggleConfig[] = [
  {
    key: 'correctRayOutliers',
    label: 'Correct ray outliers',
    description:
      // eslint-disable-next-line max-len
      'Re-runs rays that overshoot, spike outward, or dent inward against their neighbors so the polygon edges line up.',
    shouldRemap: true,
  },
  {
    key: 'debugStopLogging',
    label: 'Step stop logging',
    description: 'Logs final stop reasons, key values, and pass-fix history for each ray.',
  },
  {
    key: 'debugStepLogging',
    label: 'Step detail logging',
    description: 'Logs ratio_ab, shift_ab, de_med, L/AB diffs and step/base deltas for every ray walking step.',
  },
  {
    key: 'debugImageLogging',
    label: 'Image preview logging',
    description: 'Logs the existing probe and ray overlay image preview to the console.',
  },
]

const greenAutoMapPointsSlider = GREEN_AUTO_MAP_PRIMARY_SLIDERS.find((config) => config.key === 'rayCount')

if (!greenAutoMapPointsSlider) {
  throw new Error('Missing green auto-map points slider config')
}

export const GREEN_AUTO_MAP_POINTS_SLIDER: GreenAutoMapSliderConfig = greenAutoMapPointsSlider

const greenAutoMapLoosenessSlider = GREEN_AUTO_MAP_ADVANCED_SLIDERS.find((config) => config.key === 'loosenessBias')

if (!greenAutoMapLoosenessSlider) {
  throw new Error('Missing green auto-map looseness slider config')
}

export const GREEN_AUTO_MAP_LOOSENESS_SLIDER: GreenAutoMapSliderConfig = greenAutoMapLoosenessSlider

export const GREEN_AUTO_MAP_PANEL_SLIDERS = [
  ...GREEN_AUTO_MAP_PRIMARY_SLIDERS.filter((config) => config.key !== 'rayCount'),
  ...GREEN_AUTO_MAP_ADVANCED_SLIDERS,
]
